import { useTranslation } from "react-i18next";
import type { Document } from "../../types/document";
import styles from "./DocumentCard.module.css";

interface DocumentCardProps {
	document: Document;
	onClick: () => void;
}

export function DocumentCard({ document, onClick }: DocumentCardProps) {
	const { t } = useTranslation("documents");

	const getFileIcon = (mimeType: string): string => {
		if (mimeType.startsWith("image/")) return "pi-image";
		if (mimeType === "application/pdf") return "pi-file-pdf";
		if (
			mimeType === "application/msword" ||
			mimeType ===
				"application/vnd.openxmlformats-officedocument.wordprocessingml.document"
		)
			return "pi-file-word";
		return "pi-file";
	};

	const getCategoryColor = (category: string): string => {
		switch (category) {
			case "lab_results":
				return "#10b981";
			case "prescriptions":
				return "#3b82f6";
			case "imaging":
				return "#8b5cf6";
			case "medical_records":
				return "#ef4444";
			case "insurance":
				return "#f59e0b";
			default:
				return "#6b7280";
		}
	};

	const formatDate = (dateString: string | undefined | null): string => {
		if (!dateString) return "";
		const date = new Date(dateString);
		if (Number.isNaN(date.getTime())) return "";
		return date.toLocaleDateString(undefined, {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	const isImage = document.mimeType.startsWith("image/");

	const formatFileSize = (bytes: number): string => {
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	};

	return (
		<button type="button" className={styles.card} onClick={onClick}>
			{isImage ? (
				<div className={styles.imageWrapper}>
					<img
						src={document.fileUrl}
						alt={document.name}
						className={styles.image}
					/>
				</div>
			) : (
				<div className={styles.iconWrapper}>
					<i
						className={`pi ${getFileIcon(document.mimeType)} ${styles.icon}`}
					/>
				</div>
			)}

			<div className={styles.content}>
				<h3 className={styles.title}>{document.name}</h3>

				{document.description && (
					<p className={styles.description}>{document.description}</p>
				)}

				<div className={styles.meta}>
					<span
						className={styles.category}
						style={{ backgroundColor: getCategoryColor(document.category) }}
					>
						{t(`categories.${document.category}`)}
					</span>

					{formatDate(document.uploadedAt) && (
						<span className={styles.date}>
							{formatDate(document.uploadedAt)}
						</span>
					)}
				</div>

				<div className={styles.footer}>
					<span className={styles.fileSize}>
						{formatFileSize(document.fileSize)}
					</span>

					{document.shareCount > 0 && (
						<span className={styles.shared}>
							<i className="pi pi-users" />
							{t("card.sharedWith", { count: document.shareCount })}
						</span>
					)}
				</div>
			</div>
		</button>
	);
}
